import React, { useContext } from 'react';
import { FormLabel } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faStar, faComments, faComment, faWrench } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../store';
import { setParams } from '../reducers/paramsReducer';
import AppContext from '../context/AppContext';

// Toggle chips for the "Show games with" filter group — favorites-only, favorite
// moves, and the comment filters.
function Toggle(props: {
  active: boolean;
  icon: IconDefinition;
  label: string;
  onClick: () => void;
}): React.ReactNode {
  const { active, icon, label, onClick } = props;
  return (
    <button
      type="button"
      className={`cm-fav-toggle${active ? ' active' : ''}`}
      aria-pressed={active}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={icon} className="cm-fav-toggle-icon" />
      {label}
    </button>
  );
}

export default function GamesFiltersToggles(): React.ReactNode {
  const { isAuthenticated } = useContext(AppContext);
  const { onlyStarred, favoriteMoves, hasComments, commentedByMe, repair } = useAppSelector(
    (state) => state.paramsReducer.params,
  );
  const dispatch = useAppDispatch();

  return (
    <>
      <FormLabel className="d-block">Show games with</FormLabel>
      <div className="d-flex flex-row flex-wrap gap-2">
        {isAuthenticated ? (
          <Toggle
            active={onlyStarred === true}
            icon={faStar}
            label="Favorites only"
            onClick={() => dispatch(setParams({ onlyStarred: onlyStarred === true ? null : true }))}
          />
        ) : null}
        {isAuthenticated ? (
          <Toggle
            active={favoriteMoves === true}
            icon={faStar}
            label="Favorite moves"
            onClick={() =>
              dispatch(setParams({ favoriteMoves: favoriteMoves === true ? null : true }))
            }
          />
        ) : null}
        {/* The two repair filters. "Not repaired lately" is the one that matters day to day: it's
            how you find the games that have gone cold, which is the whole point of keeping a repair
            history at all. "Never repaired" is the stricter cut for a first pass through a library. */}
        {isAuthenticated ? (
          <Toggle
            active={repair === 'stale'}
            icon={faWrench}
            label="Not repaired lately"
            onClick={() => dispatch(setParams({ repair: repair === 'stale' ? null : 'stale' }))}
          />
        ) : null}
        {isAuthenticated ? (
          <Toggle
            active={repair === 'never'}
            icon={faWrench}
            label="Never repaired"
            onClick={() => dispatch(setParams({ repair: repair === 'never' ? null : 'never' }))}
          />
        ) : null}
        {/* Public notes are the webmaster-curated layer now that everything a player writes is
            private, so this finds a small, deliberate set rather than "any comment". Labelled for
            what it actually returns — "Comments" would read as a filter that matches nothing. */}
        <Toggle
          active={hasComments === true}
          icon={faComments}
          label="Public notes"
          onClick={() => dispatch(setParams({ hasComments: hasComments === true ? null : true }))}
        />
        {isAuthenticated ? (
          <Toggle
            active={commentedByMe === true}
            icon={faComment}
            label="My notes"
            onClick={() =>
              dispatch(setParams({ commentedByMe: commentedByMe === true ? null : true }))
            }
          />
        ) : null}
      </div>
    </>
  );
}
